// Test page for onboarding integration
// Demonstrates the complete Gmail integration flow

import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import EmailConnectionStep from '@/components/onboarding/EmailConnectionStep';
import { 
  Mail, 
  CheckCircle, 
  ArrowRight,
  ArrowLeft,
  Home
} from 'lucide-react';
import { Link } from 'react-router-dom';

export default function OnboardingTestPage() {
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState('intro');
  const [completedSteps, setCompletedSteps] = useState([]);

  const steps = [
    { id: 'intro', title: 'Welcome', description: 'Get started with CampConnect' },
    { id: 'profile', title: 'Profile Setup', description: 'Tell us about yourself' },
    { id: 'children', title: 'Children Details', description: 'Add your children' },
    { id: 'preferences', title: 'Preferences', description: 'Set your preferences' },
    { id: 'email', title: 'Email Connection', description: 'Connect your Gmail' },
    { id: 'friends', title: 'Connect Friends', description: 'Invite other families' },
    { id: 'calendar', title: 'Calendar', description: 'Connect your calendar' },
    { id: 'complete', title: 'Complete', description: 'You\'re all set!' }
  ];

  const handleEmailComplete = () => {
    setCompletedSteps(prev => [...prev, 'email']);
    setCurrentStep('friends');
  };

  const handleEmailSkip = () => {
    setCurrentStep('friends');
  };

  const handleNext = () => {
    const currentIndex = steps.findIndex(step => step.id === currentStep);
    if (currentIndex < steps.length - 1) {
      setCurrentStep(steps[currentIndex + 1].id);
    }
  };

  const handleBack = () => {
    const currentIndex = steps.findIndex(step => step.id === currentStep);
    if (currentIndex > 0) {
      setCurrentStep(steps[currentIndex - 1].id);
    }
  };

  const handleReset = () => {
    setCurrentStep('intro');
    setCompletedSteps([]);
  };

  const getStepStatus = (stepId) => {
    if (completedSteps.includes(stepId)) return 'completed';
    if (stepId === currentStep) return 'current';
    return 'pending';
  };

  const getStepIcon = (stepId) => {
    const status = getStepStatus(stepId);
    if (status === 'completed') return <CheckCircle className="w-4 h-4 text-green-600" />;
    if (status === 'current') return <div className="w-4 h-4 bg-blue-600 rounded-full" />;
    return <div className="w-4 h-4 bg-gray-300 rounded-full" />;
  };

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-3xl font-bold text-gray-900">
              Onboarding Integration Test
            </h1>
            <Link to="/Profile">
              <Button variant="outline">
                <Home className="w-4 h-4 mr-2" />
                Back to Profile
              </Button>
            </Link>
          </div>
          <p className="text-gray-600">
            Test the complete Gmail integration flow in the onboarding process
          </p>
        </div>

        {/* Progress Steps */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Onboarding Progress</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between overflow-x-auto">
              {steps.map((step, index) => (
                <div key={step.id} className="flex items-center">
                  <div className="flex flex-col items-center min-w-0">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-white border-2 border-gray-300 mb-2">
                      {getStepIcon(step.id)}
                    </div>
                    <div className="text-center">
                      <div className="text-xs font-medium text-gray-900 truncate">
                        {step.title}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        {step.description}
                      </div>
                    </div>
                  </div>
                  {index < steps.length - 1 && (
                    <div className="w-8 h-0.5 bg-gray-300 mx-2" />
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Current Step Content */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Mail className="w-5 h-5 text-indigo-600" />
              <span>Step: {steps.find(s => s.id === currentStep)?.title}</span>
              {completedSteps.includes(currentStep) && (
                <Badge variant="secondary" className="bg-green-100 text-green-800">
                  Completed
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {currentStep === 'email' ? (
              <EmailConnectionStep
                user={user}
                onComplete={handleEmailComplete}
                onSkip={handleEmailSkip}
                isActive={true}
              />
            ) : (
              <div className="text-center py-8">
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {steps.find(s => s.id === currentStep)?.title}
                  </h3>
                  <p className="text-gray-600">
                    {steps.find(s => s.id === currentStep)?.description}
                  </p>
                </div>
                
                {currentStep === 'intro' && (
                  <div className="space-y-4">
                    <p className="text-gray-600">
                      Welcome to CampConnect! This test demonstrates the complete onboarding flow including Gmail integration.
                    </p>
                    <div className="bg-blue-50 rounded-lg p-4">
                      <h4 className="font-medium text-blue-900 mb-2">What you'll test:</h4>
                      <ul className="text-sm text-blue-800 space-y-1 text-left">
                        <li>• Gmail OAuth connection flow</li>
                        <li>• Email processing with N8N webhook</li>
                        <li>• Database storage of email connections</li>
                        <li>• Error handling and retry logic</li>
                        <li>• Skip functionality</li>
                      </ul>
                    </div>
                  </div>
                )}

                {currentStep !== 'intro' && currentStep !== 'email' && (
                  <div className="space-y-4">
                    <p className="text-gray-600">
                      This step would normally contain the {steps.find(s => s.id === currentStep)?.title.toLowerCase()} setup.
                    </p>
                    <p className="text-sm text-gray-500">
                      For this test, we're focusing on the email connection step.
                    </p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between">
          <Button
            onClick={handleBack}
            disabled={currentStep === 'intro'}
            variant="outline"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>

          <div className="flex space-x-2">
            <Button
              onClick={handleReset}
              variant="outline"
            >
              Reset Test
            </Button>
            
            {currentStep !== 'email' && currentStep !== 'complete' && (
              <Button
                onClick={handleNext}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                Next
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </div>

        {/* User Info */}
        <div className="mt-8 text-center text-sm text-gray-500">
          {user ? (
            <p>Testing as: <span className="font-medium">{user.email}</span></p>
          ) : (
            <p>Please log in to test the onboarding flow</p>
          )}
        </div>
      </div>
    </div>
  );
}
